import { c as _c } from "react/compiler-runtime";
import type { OnboardingSessionConfig, WorkflowFixtureResult } from '@onefootprint/request-types';
import { Form } from '@onefootprint/ui';
import { useEffect } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import type { SandboxOutcomeFormData } from '../types';
import useSandboxOutcomeOptions from '../use-sandbox-outcome-options';
type OverallOutcomeSelectProps = {
  config?: OnboardingSessionConfig;
};
const getOverallOutcomeOptions = (config: OnboardingSessionConfig | undefined, allOptions: {
  label: string;
  value: string;
}[], isDisabled: boolean) => {
  const [pass, fail, manualReview, stepUp, evaluateRules, chooseBranch] = allOptions;
  const hideLegacy = !!config?.sandboxConfig?.hideLegacyFixtureOutcomes;
  const showStepUp = !!config?.sandboxConfig?.allowStepupOutcome;
  const options: {
    label: string;
    value: string;
  }[] = [];
  if (!hideLegacy) {
    options.push(pass, fail, manualReview);
  }
  options.push(evaluateRules, chooseBranch);
  if (showStepUp && !hideLegacy) {
    options.push(stepUp);
  }
  if (isDisabled) {
    options.push({
      label: '-',
      value: 'use_rules_outcome'
    });
  }
  return options;
};
const OverallOutcomeSelect = t0 => {
  const $ = _c(42);
  const {
    config
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      keyPrefix: "sandboxOutcome.overall-outcome"
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    t
  } = useTranslation("idv2", t1);
  const {
    overallOutcomeOptions
  } = useSandboxOutcomeOptions();
  const {
    setValue
  } = useFormContext();
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      name: "idDocOutcome"
    };
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const watchIdDocOutcome = useWatch(t2);
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      name: "docVerificationOutcome"
    };
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const watchDocVerificationOutcome = useWatch(t3);
  let t4;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = {
      name: "overallOutcome"
    };
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  const watchOverallOutcome = useWatch(t4);
  const requiresIdDoc = !!config?.sandboxConfig?.collectDocumentOutcome;
  const isDisabled = watchIdDocOutcome === "fail" || watchDocVerificationOutcome === "real";
  let t5;
  if ($[4] !== config || $[5] !== isDisabled || $[6] !== overallOutcomeOptions) {
    t5 = getOverallOutcomeOptions(config, overallOutcomeOptions, isDisabled);
    $[4] = config;
    $[5] = isDisabled;
    $[6] = overallOutcomeOptions;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  const options = t5;
  let t6;
  if ($[8] !== requiresIdDoc || $[9] !== setValue || $[10] !== watchDocVerificationOutcome || $[11] !== watchIdDocOutcome || $[12] !== watchOverallOutcome) {
    t6 = () => {
      const isStepUp = watchOverallOutcome === "step_up";
      const docFailed = watchIdDocOutcome === "fail";
      const docIsReal = watchDocVerificationOutcome === "real";
      if (!isStepUp && docFailed) {
        setValue("overallOutcome", "fail");
      } else {
        if (!isStepUp && docIsReal) {
          setValue("overallOutcome", "use_rules_outcome");
        }
      }
      if (isStepUp && watchIdDocOutcome === undefined) {
        setValue("idDocOutcome", "pass");
      } else {
        if (!isStepUp && !requiresIdDoc) {
          setValue("idDocOutcome", undefined);
        }
      }
      if (watchOverallOutcome !== "use_rules_outcome") {
        setValue("fixtureRiskSignals", undefined);
      }
    };
    $[8] = requiresIdDoc;
    $[9] = setValue;
    $[10] = watchDocVerificationOutcome;
    $[11] = watchIdDocOutcome;
    $[12] = watchOverallOutcome;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  let t7;
  if ($[14] !== setValue || $[15] !== watchDocVerificationOutcome || $[16] !== watchIdDocOutcome || $[17] !== watchOverallOutcome) {
    t7 = [setValue, watchIdDocOutcome, watchDocVerificationOutcome, watchOverallOutcome];
    $[14] = setValue;
    $[15] = watchDocVerificationOutcome;
    $[16] = watchIdDocOutcome;
    $[17] = watchOverallOutcome;
    $[18] = t7;
  } else {
    t7 = $[18];
  }
  useEffect(t6, t7);
  let t8;
  if ($[19] !== config?.sandboxConfig?.availableFixtureReasonCodes) {
    t8 = config?.sandboxConfig?.availableFixtureReasonCodes || [];
    $[19] = config?.sandboxConfig?.availableFixtureReasonCodes;
    $[20] = t8;
  } else {
    t8 = $[20];
  }
  const fixtureRiskSignalOptions = t8;
  let t9;
  if ($[21] !== t) {
    t9 = t(_temp);
    $[21] = t;
    $[22] = t9;
  } else {
    t9 = $[22];
  }
  let t10;
  if ($[23] !== t) {
    t10 = t(_temp2);
    $[23] = t;
    $[24] = t10;
  } else {
    t10 = $[24];
  }
  let t11;
  if ($[25] !== t10 || $[26] !== t9) {
    t11 = {
      use_rules_outcome: t9,
      choose_branch_outcomes: t10
    };
    $[25] = t10;
    $[26] = t9;
    $[27] = t11;
  } else {
    t11 = $[27];
  }
  const optionHints = t11;
  let t12;
  if ($[28] !== t) {
    t12 = t(_temp3);
    $[28] = t;
    $[29] = t12;
  } else {
    t12 = $[29];
  }
  const t13 = optionHints[watchOverallOutcome];
  let t14;
  if ($[30] !== isDisabled || $[31] !== options || $[32] !== t12 || $[33] !== t13) {
    t14 = <div className="flex items-center justify-between"><Form.Select name="overallOutcome" label={t12} size="compact" disabled={isDisabled} hint={t13} options={options} /></div>;
    $[30] = isDisabled;
    $[31] = options;
    $[32] = t12;
    $[33] = t13;
    $[34] = t14;
  } else {
    t14 = $[34];
  }
  let t15;
  if ($[35] !== fixtureRiskSignalOptions || $[36] !== t || $[37] !== watchOverallOutcome) {
    t15 = watchOverallOutcome === "use_rules_outcome" && <Form.MultiSelect name="fixtureRiskSignals" size="compact" hideSelectAll={true} label={t(_temp4)} options={fixtureRiskSignalOptions.map(_temp5)} />;
    $[35] = fixtureRiskSignalOptions;
    $[36] = t;
    $[37] = watchOverallOutcome;
    $[38] = t15;
  } else {
    t15 = $[38];
  }
  let t16;
  if ($[39] !== t14 || $[40] !== t15) {
    t16 = <div className="flex flex-col gap-4">{t14}{t15}</div>;
    $[39] = t14;
    $[40] = t15;
    $[41] = t16;
  } else {
    t16 = $[41];
  }
  return t16;
};
export default OverallOutcomeSelect;
function _temp($) {
  return $.outcome.options["evaluate-rules"].hint;
}
function _temp2($_0) {
  return $_0.outcome.options["choose-branch"].hint;
}
function _temp3($_1) {
  return $_1.title;
}
function _temp4($_2) {
  return $_2.outcome.options["evaluate-rules"].fixtureRiskSignals;
}
function _temp5(code) {
  return {
    label: code,
    value: code
  };
}